import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Check, AlertTriangle, PackageCheck, Loader2, ArrowRight } from "lucide-react";
import { translations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = { order_number: "", postal_code: "", email: "" };

const DEFAULT_STYLES = {
  colors: {
    background: "#FFFFFF", text: "#0A0A0A", button: "#0A0A0A", buttonText: "#FFFFFF",
    warning: "#B26B00", error: "#E50000", success: "#00C853",
  },
  fontSize: { title: 30, body: 14 },
};

export default function RecallModal({ open, onClose, lang = "en", config = null, shopOrigin = null }) {
  const t = config?.texts?.[lang] || translations[lang]?.modal || translations.en.modal;
  const styles = config?.styles || DEFAULT_STYLES;
  const c = { ...DEFAULT_STYLES.colors, ...(styles.colors || {}) };
  const fs = { ...DEFAULT_STYLES.fontSize, ...(styles.fontSize || {}) };
  const [form, setForm] = useState(emptyForm);
  const [validation, setValidation] = useState(null); // server response
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState("form"); // 'form' | 'success'
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setValidation(null);
      setStep("form");
      setTouched({});
    }
  }, [open]);

  const emailValid = EMAIL_RE.test(form.email);
  const canCheck = form.order_number.trim() && form.postal_code.trim() && emailValid;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setValidation(null); // reset verification when inputs change
  };

  const handleCheck = async () => {
    if (!canCheck) {
      setTouched({ order_number: true, postal_code: true, email: true });
      return;
    }
    setChecking(true);
    try {
      const { data } = await axios.post(`${API}/recall/validate`, { ...form, shop_origin: shopOrigin });
      setValidation(data);
    } catch (e) {
      toast.error(t.genericError);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/recall/submit`, { ...form, lang, shop_origin: shopOrigin });
      setStep("success");
    } catch (e) {
      toast.error(t.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const recallReady = validation?.valid && validation?.recall_enabled;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="recall-modal">
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-xl backdrop-saturate-150"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
        <motion.div
          className="relative w-full max-w-lg bg-white shadow-[0_32px_64px_rgba(0,0,0,0.15)] rounded-xl overflow-hidden flex flex-col border border-black/5"
          style={{ backgroundColor: c.background, color: c.text, fontSize: fs.body }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="absolute top-5 right-5 text-[#525252] hover:text-black transition-colors z-10"
            aria-label="Close"
          >
            <X strokeWidth={1.5} className="w-5 h-5" />
          </button>

          {step === "form" ? (
            <>
              <div className="px-8 pt-8 pb-4 flex flex-col gap-2">
                <p className="text-xs uppercase tracking-widest font-bold text-[#525252]">{t.badge}</p>
                <h2 className="font-heading font-extrabold tracking-tight text-3xl" style={{ fontSize: fs.title, color: c.text }}>{t.title}</h2>
                <p className="text-sm text-[#525252]" style={{ fontSize: fs.body }}>
                  {t.subtitle}
                </p>
              </div>

              <div className="px-8 pb-8 flex flex-col gap-6">
                <Field
                  testid="order-number-input"
                  label={t.orderLabel}
                  placeholder={t.orderPlaceholder}
                  value={form.order_number}
                  onChange={(v) => setField("order_number", v.toUpperCase().replace(/\s+/g, ""))}
                  onBlur={() => setTouched((t) => ({ ...t, order_number: true }))}
                  invalid={touched.order_number && !form.order_number.trim()}
                  errorText={t.orderRequired}
                />
                <Field
                  testid="postal-code-input"
                  label={t.postalLabel}
                  placeholder={t.postalPlaceholder}
                  value={form.postal_code}
                  onChange={(v) => setField("postal_code", v)}
                  onBlur={() => setTouched((t) => ({ ...t, postal_code: true }))}
                  invalid={touched.postal_code && !form.postal_code.trim()}
                  errorText={t.postalRequired}
                />
                <Field
                  testid="email-input"
                  label={t.emailLabel}
                  placeholder={t.emailPlaceholder}
                  value={form.email}
                  onChange={(v) => setField("email", v)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  invalid={touched.email && form.email.length > 0 && !emailValid}
                  valid={emailValid}
                  errorText={t.emailInvalid}
                />

                {/* Verification result */}
                <AnimatePresence>
                  {validation && !validation.valid && (
                    <motion.div
                      key="error"
                      data-testid="validation-error"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex items-start gap-3 border-l-2 border-[#E50000] bg-[#FFF1F1] px-4 py-3"
                      style={{ borderColor: c.error }}
                    >
                      <AlertTriangle strokeWidth={1.5} className="w-5 h-5 shrink-0 mt-0.5" style={{ color: c.error }} />
                      <p className="text-sm" style={{ color: c.error, fontSize: fs.body }}>
                        {validation.reason === "cancelled"
                          ? t.reasonCancelled
                          : validation.reason === "company"
                          ? t.reasonCompany
                          : validation.reason === "invalid_email"
                          ? t.emailInvalid
                          : t.reasonNotFound}
                      </p>
                    </motion.div>
                  )}

                  {recallReady && (
                    <motion.div
                      key="found"
                      data-testid="order-found"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-black/10 bg-stone-50 px-4 py-3"
                    >
                      <div className="flex items-center gap-2" style={{ color: c.success }}>
                        <Check strokeWidth={2} className="w-4 h-4" />
                        <span className="text-xs uppercase tracking-widest font-bold">{t.orderVerified}</span>
                      </div>
                      <div className="mt-2 text-sm text-[#0A0A0A]">
                        <span className="font-semibold">{validation.order.order_number}</span>
                        <span className="text-[#525252]"> · {validation.order.customer_name}</span>
                      </div>
                    </motion.div>
                  )}

                  {recallReady && validation.is_older_than_14_days && (
                    <motion.div
                      key="warning"
                      data-testid="older-than-14-warning"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 bg-[#FFF4E5] border px-4 py-3"
                      style={{ color: c.warning, borderColor: c.warning }}
                    >
                      <AlertTriangle strokeWidth={1.5} className="w-5 h-5 shrink-0 mt-0.5" />
                      <p className="text-sm" style={{ fontSize: fs.body }}>{t.warning14}</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action */}
                {!recallReady ? (
                  <button
                    data-testid="verify-order-btn"
                    onClick={handleCheck}
                    disabled={checking}
                    className="w-full h-14 font-semibold text-lg hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: c.button, color: c.buttonText }}
                  >
                    {checking ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t.verifyButton} <ArrowRight strokeWidth={1.5} className="w-5 h-5" /></>}
                  </button>
                ) : (
                  <button
                    data-testid="recall-submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full h-14 font-semibold text-lg hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ backgroundColor: c.button, color: c.buttonText }}
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t.recallButton} <PackageCheck strokeWidth={1.5} className="w-5 h-5" /></>}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="px-8 py-12 flex flex-col items-center text-center gap-5" data-testid="success-screen" aria-live="polite">
              <div className="w-28 h-28 rounded-full bg-stone-50 border border-black/5 flex items-center justify-center overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1656543802898-41c8c46683a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwY2FyZGJvYXJkJTIwYm94JTIwcGFja2FnZXxlbnwwfHx8fDE3ODM1ODc4OTB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Package"
                  className="w-full h-full object-cover"
                />
              </div>
              <h2 className="font-heading font-extrabold tracking-tight text-3xl" style={{ fontSize: fs.title, color: c.text }}>{t.successTitle}</h2>
              <p className="text-sm text-[#525252] max-w-sm" style={{ fontSize: fs.body }}>
                {t.successBodyPre}{" "}
                <span className="font-semibold" style={{ color: c.text }}>{form.email}</span>
                {t.successBodyPost}
              </p>
              <button
                data-testid="success-close-btn"
                onClick={onClose}
                className="mt-2 h-12 px-10 font-semibold hover:opacity-90 active:scale-[0.98] transition-all"
                style={{ backgroundColor: c.button, color: c.buttonText }}
              >
                {t.closeButton}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function Field({ testid, label, placeholder, value, onChange, onBlur, invalid, valid, errorText }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs uppercase tracking-widest font-bold text-[#525252]">{label}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-invalid={invalid ? "true" : "false"}
          className={`w-full h-14 px-4 pr-10 rounded-none border-b-2 bg-stone-50 outline-none text-base transition-colors ${
            invalid ? "border-[#E50000] focus:border-[#E50000]" : "border-black/20 focus:border-black"
          }`}
        />
        {valid && !invalid && (
          <Check strokeWidth={2} className="w-5 h-5 text-[#00C853] absolute right-3 top-1/2 -translate-y-1/2" />
        )}
        {invalid && (
          <X strokeWidth={2} className="w-5 h-5 text-[#E50000] absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>
      {invalid && (
        <p className="text-xs text-[#E50000]" aria-live="polite" data-testid={`${testid}-error`}>
          {errorText}
        </p>
      )}
    </div>
  );
}
